import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Switch,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

const TabTabelleImpostazioni = ({ tariffe, onUpdateTariff, onDeleteTariff, onAddTariff }) => {
  const [editDialog, setEditDialog] = useState({ open: false, tariff: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });

  const handleToggleActive = (id) => {
    const tariff = tariffe.find((t) => t.id === id);
    if (tariff) {
      onUpdateTariff(id, { ...tariff, attivo: !tariff.attivo });
    }
  };

  const handleOpenEditDialog = (tariff) => {
    setEditDialog({ open: true, tariff: { ...tariff } });
  };

  const handleCloseEditDialog = () => {
    setEditDialog({ open: false, tariff: null });
  };

  const handleSaveEdit = () => {
    if (editDialog.tariff) {
      onUpdateTariff(editDialog.tariff.id, editDialog.tariff);
      handleCloseEditDialog();
    }
  };

  const handleEditChange = (field, value) => {
    setEditDialog((prev) => ({
      ...prev,
      tariff: {
        ...prev.tariff,
        [field]: value,
      },
    }));
  };

  const handleNestedEditChange = (parent, field, value) => {
    setEditDialog((prev) => ({
      ...prev,
      tariff: {
        ...prev.tariff,
        [parent]: {
          ...prev.tariff[parent],
          [field]: value,
        },
      },
    }));
  };

  const handleOpenDeleteDialog = (id) => {
    setDeleteDialog({ open: true, id });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({ open: false, id: null });
  };

  const handleConfirmDelete = () => {
    if (deleteDialog.id) {
      onDeleteTariff(deleteDialog.id);
      handleCloseDeleteDialog();
    }
  };

  const columns = [
    {
      field: "attivo",
      headerName: "Stato",
      width: 100,
      renderCell: (params) => (
        <Switch
          checked={params.row.attivo || false}
          onChange={() => handleToggleActive(params.row.id)}
          color="primary"
          inputProps={{ 
            'aria-label': `Toggle stato tariffa ${params.row.id}` 
          }}
        />
      ),
    },
    {
      field: "fornitore",
      headerName: "Fornitore",
      width: 150,
    },
    {
      field: "nomeOfferta",
      headerName: "Nome Offerta",
      width: 200,
    },
    {
      field: "tipo",
      headerName: "Tipo",
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={
            params.value === "luce"
              ? "warning"
              : params.value === "gas"
              ? "info"
              : "secondary"
          }
        />
      ),
    },
    {
      field: "prezzoMateriaPrima",
      headerName: "Prezzo MP",
      width: 150,
      valueGetter: (params) => {
        if (params.row.prezzoMateriaPrima) {
          return `${params.row.prezzoMateriaPrima.valore} ${params.row.prezzoMateriaPrima.unita}`;
        }
        return "-";
      },
    },
    {
      field: "spread",
      headerName: "Spread",
      width: 120,
      valueGetter: (params) => (params.row.spread ? `€ ${params.row.spread}` : "-"),
    },
    {
      field: "pcv",
      headerName: "PCV Mensile",
      width: 120,
      valueGetter: (params) => {
        if (params.row.pcv?.valoreMensile) {
          return `€ ${params.row.pcv.valoreMensile}`;
        }
        return "-";
      },
    },
    {
      field: "confidenceScore",
      headerName: "Confidence",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={`${params.value}%`}
          size="small"
          color={params.value >= 80 ? "success" : params.value >= 70 ? "warning" : "error"}
        />
      ),
    },
    {
      field: "ultimoAggiornamento",
      headerName: "Ultimo Agg.",
      width: 150,
      valueGetter: (params) => {
        if (params.row.ultimoAggiornamento) {
          return new Date(params.row.ultimoAggiornamento).toLocaleString("it-IT");
        }
        return "-";
      },
    },
    {
      field: "actions",
      headerName: "Azioni",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleOpenEditDialog(params.row)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleOpenDeleteDialog(params.row.id)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">
          Tariffe salvate ({tariffe.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            const newTariff = {
              id: Date.now(),
              fornitore: "",
              nomeOfferta: "",
              tipo: "luce",
              prezzoMateriaPrima: { valore: 0, unita: "€/kWh", fasceOrarie: false },
              spread: 0,
              pcv: { valoreMensile: 0, valoreAnnuo: 0 },
              confidenceScore: 100,
              needsReview: false,
              attivo: true,
              timestamp: new Date().toISOString(),
            };
            handleOpenEditDialog(newTariff);
          }}
        >
          Nuova Tariffa
        </Button>
      </Box>

      {tariffe.length === 0 ? (
        <Alert severity="info">
          Nessuna tariffa salvata. Carica un documento nel Tab "Carica & Estrai" o crea una nuova tariffa manualmente.
        </Alert>
      ) : (
        <Paper sx={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={tariffe}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            checkboxSelection
            disableSelectionOnClick
            sx={{
              "& .MuiDataGrid-row": {
                cursor: "pointer",
              },
            }}
          />
        </Paper>
      )}

      {/* Edit Dialog */}
      <Dialog
        open={editDialog.open}
        onClose={handleCloseEditDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editDialog.tariff?.id ? "Modifica Tariffa" : "Nuova Tariffa"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <TextField
              label="Fornitore"
              value={editDialog.tariff?.fornitore || ""}
              onChange={(e) => handleEditChange("fornitore", e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Nome Offerta"
              value={editDialog.tariff?.nomeOfferta || ""}
              onChange={(e) => handleEditChange("nomeOfferta", e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Tipo"
              select
              value={editDialog.tariff?.tipo || "luce"}
              onChange={(e) => handleEditChange("tipo", e.target.value)}
              fullWidth
            >
              <MenuItem value="luce">Luce</MenuItem>
              <MenuItem value="gas">Gas</MenuItem>
              <MenuItem value="dual">Dual</MenuItem>
            </TextField>

            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Prezzo Materia Prima
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Valore"
                type="number"
                value={editDialog.tariff?.prezzoMateriaPrima?.valore || ""}
                onChange={(e) =>
                  handleNestedEditChange("prezzoMateriaPrima", "valore", e.target.value)
                }
                fullWidth
              />
              <TextField
                label="Unità"
                select
                value={editDialog.tariff?.prezzoMateriaPrima?.unita || "€/kWh"}
                onChange={(e) =>
                  handleNestedEditChange("prezzoMateriaPrima", "unita", e.target.value)
                }
                fullWidth
              >
                <MenuItem value="€/kWh">€/kWh</MenuItem>
                <MenuItem value="€/Smc">€/Smc</MenuItem>
              </TextField>
            </Box>

            <TextField
              label="Spread"
              type="number"
              value={editDialog.tariff?.spread || ""}
              onChange={(e) => handleEditChange("spread", e.target.value)}
              fullWidth
            />

            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              PCV (Prezzo Commerciale Variabile)
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Valore Mensile (€)"
                type="number"
                value={editDialog.tariff?.pcv?.valoreMensile || ""}
                onChange={(e) => handleNestedEditChange("pcv", "valoreMensile", e.target.value)}
                fullWidth
              />
              <TextField
                label="Valore Annuo (€)"
                type="number"
                value={editDialog.tariff?.pcv?.valoreAnnuo || ""}
                onChange={(e) => handleNestedEditChange("pcv", "valoreAnnuo", e.target.value)}
                fullWidth
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Annulla</Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={
              !editDialog.tariff?.fornitore || !editDialog.tariff?.nomeOfferta
            }
          >
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Conferma Eliminazione</DialogTitle>
        <DialogContent>
          Sei sicuro di voler eliminare questa tariffa? L'operazione non può essere annullata.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Annulla</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Elimina
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TabTabelleImpostazioni;
